import { type ComponentType, type ReactNode } from 'react';

import { type QueryClient, type UseQueryResult } from '@tanstack/react-query';
import { CameraIcon, GiftIcon, PackageIcon, TicketIcon } from 'lucide-react';

import { getItemsQueryOptions } from '@/api/items/getItems';
import { Panel } from '@/components/ui/panel';
import { PanelSkeleton } from '@/components/ui/panelSkeleton';
import { Text } from '@/components/ui/typography';
import { ItemsTable } from '@/features/catalog/components/itemsTable';
import { getGiftsQueryOptions } from '@/features/gift/api/useGifts';
import { GiftsTable } from '@/features/gift/components/giftsTable';
import { seasonPassesQuery } from '@/features/seasonPass/api/getSeasonPasses';
import { SeasonPassesTable } from '@/features/seasonPass/components/seasonPassesTable';
import {
  getSnapshots,
  snapshotsQueryKey,
} from '@/features/store/snapshot/api/getSnapshots';
import { SnapshotsTable } from '@/features/store/snapshot/components/snapshotsTable';
import {
  type AppScope,
  scopeQueryOptions,
  useAppQuery,
} from '@/hooks/useAppQuery';

// ponytail: one page each at admin-api's cap (1000 items, 200 otherwise); the
// badge keeps the real total.
// Season passes are items too, but they get their own panel.
const readyItems = getItemsQueryOptions({
  state: 'ready',
  type: ['item', 'bundle', 'currency'],
});
const readyGifts = getGiftsQueryOptions(
  { state: 'ready' },
  { limit: 200, offset: 0 },
);
const readySeasonPasses = seasonPassesQuery({
  state: 'ready',
  sort_by: 'name',
  sort_order: 'asc',
  limit: 200,
  offset: 0,
});
// Under `all`, so every snapshot mutation's invalidation reaches it. Oldest
// effective date first: the order they go live in.
const readySnapshots = {
  queryKey: [...snapshotsQueryKey.all, 'ready'],
  queryFn: () =>
    getSnapshots({
      state: 'ready',
      sort_by: 'effective_at',
      sort_order: 'asc',
      limit: 200,
      offset: 0,
    }),
};

/** Warm the preview's queries, so the dialog opens on data. */
export const prefetchPublishPreview = (
  queryClient: QueryClient,
  scope: AppScope,
) => {
  // One call each: the four options carry different data types.
  void queryClient.prefetchQuery(scopeQueryOptions(scope, readyItems));
  void queryClient.prefetchQuery(scopeQueryOptions(scope, readyGifts));
  void queryClient.prefetchQuery(scopeQueryOptions(scope, readySeasonPasses));
  void queryClient.prefetchQuery(scopeQueryOptions(scope, readySnapshots));
};

interface PreviewPanelProps<T> {
  query: UseQueryResult<T>;
  icon: ComponentType<{ className?: string }>;
  title: string;
  skeletonRows: number;
  isEmpty: (data: T) => boolean;
  children: (data: T) => ReactNode;
}

// Renders nothing when empty, so the dialog can tell when nothing ships.
const PreviewPanel = <T extends { pagination: { total: number } }>({
  query: { data, isPending, isError },
  icon: Icon,
  title,
  skeletonRows,
  isEmpty,
  children,
}: PreviewPanelProps<T>) => {
  if (isPending) return <PanelSkeleton rows={skeletonRows} />;

  const heading = (
    <>
      <Icon className="size-4" aria-hidden />
      {title}
    </>
  );
  if (isError) {
    return (
      <Panel title={heading}>
        <Text variant="muted">Could not load the {title.toLowerCase()}.</Text>
      </Panel>
    );
  }
  if (isEmpty(data)) return null;

  return (
    <Panel title={heading} count={data.pagination.total}>
      {children(data)}
    </Panel>
  );
};

/** The READY content a sandbox publish would ship. */
export const PublishPreview = () => {
  const items = useAppQuery(readyItems);
  const gifts = useAppQuery(readyGifts);
  const seasonPasses = useAppQuery(readySeasonPasses);
  const snapshots = useAppQuery(readySnapshots);

  return (
    <>
      <PreviewPanel
        query={items}
        icon={PackageIcon}
        title="Catalog elements"
        skeletonRows={6}
        isEmpty={(data) => data.items.length === 0}
      >
        {(data) => <ItemsTable items={data.items} />}
      </PreviewPanel>
      <PreviewPanel
        query={gifts}
        icon={GiftIcon}
        title="Gifts"
        skeletonRows={3}
        isEmpty={(data) => data.gifts.length === 0}
      >
        {/* Shown as it will be once this publish lands. */}
        {(data) => <GiftsTable gifts={data.gifts} published />}
      </PreviewPanel>
      <PreviewPanel
        query={seasonPasses}
        icon={TicketIcon}
        title="Season passes"
        skeletonRows={1}
        isEmpty={(data) => data.season_passes.length === 0}
      >
        {(data) => <SeasonPassesTable seasonPasses={data.season_passes} />}
      </PreviewPanel>
      <PreviewPanel
        query={snapshots}
        icon={CameraIcon}
        title="Store snapshots"
        skeletonRows={3}
        isEmpty={(data) => data.snapshots.length === 0}
      >
        {(data) => <SnapshotsTable snapshots={data.snapshots} />}
      </PreviewPanel>
    </>
  );
};
